import React from 'react';
import ActionsHeaderHelp from '../../../components/ActionsHeaderHelp';
import Button from '../../../components/Button';
import RichContent from '../../../components/RichContent';
import {
  StandardTable, StandardTableBody, StandardTableCell, StandardTableHeader,
  StandardTableHeaderCell, StandardTableRow,
} from '../../../components/StandardTable';
import { DeleteIcon, EditIcon, OpenExternalIcon } from '../../../components/icons';
import { formatDate } from '../../lib/format';
import {
  ENDORSER_KIND_LABELS,
  endorsementSourceTypeLabel,
  endorserDisplayName,
  type EndorsementRow,
} from '../../../constants/endorsements';

const muted = 'text-xs text-text-tertiary dark:text-text-dark-tertiary';

interface EndorsementListProps<Row extends EndorsementRow> {
  rows: Row[];
  onEdit: (row: Row) => void;
  onDelete: (row: Row) => void;
  /** What the edit / delete icons do on this surface (the Actions header legend). */
  actionNotes: { edit: string; delete: string };
  /** Surface-specific line under the endorser (e.g. "Edited by hand by …"). */
  renderNote?: (row: Row) => React.ReactNode;
  emptyText?: string;
}

/**
 * The one admin endorsement list, for candidate profiles and ballot measures
 * (ENDORSEMENTS_PRD §5.5): Endorser (with what it is linked to and the
 * statement), Source, Date, Actions.
 */
function EndorsementList<Row extends EndorsementRow>({
  rows, onEdit, onDelete, actionNotes, renderNote, emptyText = 'None on file.',
}: EndorsementListProps<Row>) {
  if (rows.length === 0) return <p className={muted}>{emptyText}</p>;

  return (
    <StandardTable>
      <StandardTableHeader>
        <StandardTableRow>
          <StandardTableHeaderCell>Endorser</StandardTableHeaderCell>
          <StandardTableHeaderCell>Source</StandardTableHeaderCell>
          <StandardTableHeaderCell columnType="fit">Date</StandardTableHeaderCell>
          <StandardTableHeaderCell columnType="actions" align="right">
            <span className="inline-flex items-center gap-1.5">
              Actions
              <ActionsHeaderHelp
                icons={[
                  { key: 'edit' as const, note: actionNotes.edit },
                  { key: 'delete' as const, note: actionNotes.delete },
                ]}
              />
            </span>
          </StandardTableHeaderCell>
        </StandardTableRow>
      </StandardTableHeader>
      <StandardTableBody>
        {rows.map((row) => {
          const name = endorserDisplayName(row);
          const kind = row.endorser_kind && row.endorser_kind !== 'text' ? ENDORSER_KIND_LABELS[row.endorser_kind] : null;
          const typeLabel = endorsementSourceTypeLabel(row.source_type);
          const sourceLabel = row.source_name || (row.source_url ? 'Source' : null);
          return (
            <StandardTableRow key={row.id}>
              <StandardTableCell>
                <span className="font-medium text-text-primary dark:text-text-dark-primary">{name}</span>
                {kind && <span className={`block ${muted}`}>Linked · {kind}</span>}
                {row.statement && <RichContent html={row.statement} className="mt-1 text-sm text-text-secondary dark:text-text-dark-secondary" />}
                {renderNote?.(row)}
              </StandardTableCell>
              <StandardTableCell>
                {sourceLabel ? (
                  <>
                    {typeLabel && <span className={`block ${muted}`}>{typeLabel}</span>}
                    {row.source_url ? (
                      <a href={row.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm underline">
                        {sourceLabel} <OpenExternalIcon className="h-3 w-3" />
                      </a>
                    ) : <span className="text-sm">{sourceLabel}</span>}
                  </>
                ) : <span className={muted}>No source</span>}
              </StandardTableCell>
              <StandardTableCell>{row.endorsed_on ? formatDate(row.endorsed_on) : '—'}</StandardTableCell>
              <StandardTableCell>
                <span className="flex shrink-0 items-center justify-end gap-1">
                  <Button variant="ghost" size="sm" icon={EditIcon} ariaLabel={`Edit the endorsement by ${name}`} onClick={() => onEdit(row)} />
                  <Button variant="ghost" size="sm" icon={DeleteIcon} ariaLabel={`Remove the endorsement by ${name}`} onClick={() => onDelete(row)} />
                </span>
              </StandardTableCell>
            </StandardTableRow>
          );
        })}
      </StandardTableBody>
    </StandardTable>
  );
}

export default EndorsementList;
